// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Eye, Gavel, UserPlus } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  FilterSelect,
  FiltersToolbar,
  Input,
  Label,
  Textarea,
  formatDateTimeVn,
  useToast,
} from "@chotca/ui";
import type { AbuseReport } from "@chotca/config";
import { useAdminAbuseReports } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { getSdk } from "@chotca/api-client";
import {
  DataTable,
  usePagination,
  type Column,
  type RowAction,
} from "@/components/data-table";

type AbuseRow = AbuseReport;

const STATUS_TONE = {
  PENDING: "sun",
  UNDER_REVIEW: "lime-soft",
  RESOLVED: "mist",
  REJECTED: "danger-soft",
} as const;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function targetHref(targetType: AbuseReport["targetType"], targetId: string) {
  switch (targetType) {
    case "USER":
      return `/accounts/${targetId}`;
    case "ORGANIZATION":
      return `/orgs/${targetId}`;
    case "POSTING":
      return `/postings/${targetId}`;
    case "RATING":
      return null;
    default:
      return null;
  }
}

export function AbuseClient() {
  const t = useTranslations("admin");
  const filters = useTableFilters({ status: {} });
  const status = filters.applied.status ?? "";
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminAbuseReports({
    ...(status ? { status } : {}),
    page,
    limit: pageSize,
  });
  const rows = (data?.items ?? []) as AbuseRow[];

  const qc = useQueryClient();
  const { toast } = useToast();
  const [pendingId, setPendingId] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<AbuseRow | null>(null);
  const [resolveFor, setResolveFor] = React.useState<AbuseRow | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "abuse"] });

  const sdk = () => getSdk();

  const assignMut = useMutation({
    mutationFn: (id: string) => sdk().admin.assignSelfAbuse(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      toast({ tone: "success", title: t("abuseAssignSuccess") });
      refresh();
    },
    onError: () => toast({ tone: "error", title: t("abuseGenericError") }),
  });

  const columns: Column<AbuseRow>[] = [
    {
      key: "time",
      header: t("abuseColTime"),
      cell: (r) => (
        <span className="whitespace-nowrap text-xs">
          {formatDateTimeVn(new Date(r.createdAt))}
        </span>
      ),
    },
    {
      key: "target",
      header: t("abuseColTarget"),
      cell: (r) => {
        const href = targetHref(r.targetType, r.targetId);
        return (
          <div className="flex flex-col">
            <span className="text-xs font-semibold">
              {t(`abuseTarget_${r.targetType}`)}
            </span>
            {href ? (
              <a
                href={href}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 font-mono text-[11px] text-ash hover:text-ink"
              >
                {r.targetId.slice(0, 12)}
                <ExternalLink size={10} />
              </a>
            ) : (
              <span className="font-mono text-[11px] text-ash">
                {r.targetId.slice(0, 12)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "reporter",
      header: t("abuseColReporter"),
      cell: (r) => (
        <span className="font-mono text-xs">
          {t(`actorRole_${r.reporterRole}` as Parameters<typeof t>[0]) ??
            r.reporterRole}
          <span className="ml-1 text-ash">{r.reporterUserId.slice(0, 8)}</span>
        </span>
      ),
    },
    {
      key: "reason",
      header: t("abuseColReason"),
      cell: (r) => (
        <span className="line-clamp-2 max-w-md text-xs text-ash">
          {r.reason}
        </span>
      ),
    },
    {
      key: "evidence",
      header: t("abuseColEvidence"),
      cell: (r) =>
        r.evidenceKey ? (
          <span className="font-mono text-[11px] text-ash">
            {r.evidenceKey.slice(0, 16)}
          </span>
        ) : (
          <span className="text-xs text-ash">—</span>
        ),
    },
    {
      key: "status",
      header: t("abuseColStatus"),
      cell: (r) => (
        <Badge
          tone={STATUS_TONE[r.status as keyof typeof STATUS_TONE] ?? "mist"}
        >
          {t(`abuseStatus_${r.status}`)}
        </Badge>
      ),
    },
  ];

  const rowActions: RowAction<AbuseRow>[] = [
    {
      key: "view",
      label: t("abuseActionOpenDetail"),
      icon: Eye,
      onClick: (r) => setDetail(r),
    },
    {
      key: "assign",
      label: t("abuseActionAssign"),
      icon: UserPlus,
      onClick: (r) => assignMut.mutate(r.id),
      show: (r) => r.status === "PENDING",
      loading: (r) => assignMut.isPending && pendingId === r.id,
    },
    {
      key: "resolve",
      label: t("abuseActionResolve"),
      icon: Gavel,
      onClick: (r) => setResolveFor(r),
      show: (r) => r.status === "PENDING" || r.status === "UNDER_REVIEW",
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("abuseTitle")}</h1>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder={t("abuseFilterAll")}
          options={[
            { value: "PENDING", label: t("abuseFilterPending") },
            { value: "UNDER_REVIEW", label: t("abuseFilterUnderReview") },
            { value: "RESOLVED", label: t("abuseFilterResolved") },
            { value: "REJECTED", label: t("abuseFilterRejected") },
          ]}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(r) => r.id}
        rowActions={rowActions}
        onRowClick={(r) => setDetail(r)}
        emptyText={t("abuseEmpty")}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <DetailDialog report={detail} onClose={() => setDetail(null)} />
      <ResolveDialog
        report={resolveFor}
        onClose={() => setResolveFor(null)}
        onResolved={() => {
          setResolveFor(null);
          refresh();
        }}
      />
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-mist py-2 last:border-0">
      <span className="text-xs font-semibold text-ash">{label}</span>
      <div className="col-span-2 wrap-break-word text-sm text-ink">{value}</div>
    </div>
  );
}

function DetailDialog({
  report,
  onClose,
}: {
  report: AbuseReport | null;
  onClose: () => void;
}) {
  const t = useTranslations("admin");
  if (!report) return null;
  const href = targetHref(report.targetType, report.targetId);
  return (
    <Dialog
      open={!!report}
      onClose={onClose}
      title={t("abuseDetailTitle")}
      size="lg"
    >
      <div className="text-sm">
        <DetailRow
          label={t("abuseDetailCreatedAt")}
          value={formatDateTimeVn(new Date(report.createdAt))}
        />
        <DetailRow
          label={t("abuseColStatus")}
          value={t(`abuseStatus_${report.status}`)}
        />
        <DetailRow
          label={t("abuseDetailTarget")}
          value={
            <span>
              <span className="font-semibold">
                {t(`abuseTarget_${report.targetType}`)}
              </span>
              {" — "}
              {href ? (
                <a
                  href={href}
                  className="inline-flex items-center gap-1 font-mono text-xs text-lime-700 hover:underline"
                >
                  {report.targetId}
                  <ExternalLink size={10} />
                </a>
              ) : (
                <span className="font-mono text-xs">{report.targetId}</span>
              )}
            </span>
          }
        />
        <DetailRow
          label={t("abuseDetailReporter")}
          value={
            <span className="font-mono text-xs">
              {report.reporterRole} · {report.reporterUserId}
            </span>
          }
        />
        <DetailRow
          label={t("abuseDetailReason")}
          value={<span className="whitespace-pre-wrap">{report.reason}</span>}
        />
        {report.evidenceKey ? (
          <DetailRow
            label={t("abuseDetailEvidence")}
            value={
              <span className="font-mono text-xs">{report.evidenceKey}</span>
            }
          />
        ) : null}
        {report.assignedAdminId ? (
          <DetailRow
            label={t("abuseDetailAssignedAdmin")}
            value={
              <span className="font-mono text-xs">
                {report.assignedAdminId}
              </span>
            }
          />
        ) : null}
        {report.adminNote ? (
          <DetailRow
            label={t("abuseDetailAdminNote")}
            value={
              <span className="whitespace-pre-wrap">{report.adminNote}</span>
            }
          />
        ) : null}
        {report.resolvedAt ? (
          <DetailRow
            label={t("abuseDetailResolvedAt")}
            value={formatDateTimeVn(new Date(report.resolvedAt))}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function ResolveDialog({
  report,
  onClose,
  onResolved,
}: {
  report: AbuseReport | null;
  onClose: () => void;
  onResolved: () => void;
}) {
  const t = useTranslations("admin");
  const { toast } = useToast();
  const [outcome, setOutcome] = React.useState<"RESOLVED" | "REJECTED" | "">(
    "",
  );
  const [note, setNote] = React.useState("");
  const [hideRatingId, setHideRatingId] = React.useState("");
  const [suspendUserId, setSuspendUserId] = React.useState("");
  const [refund, setRefund] = React.useState("");

  React.useEffect(() => {
    if (report) {
      setOutcome("");
      setNote("");
      setHideRatingId(report.targetType === "RATING" ? report.targetId : "");
      setSuspendUserId(report.targetType === "USER" ? report.targetId : "");
      setRefund("");
    }
  }, [report]);

  const mut = useMutation({
    mutationFn: async (id: string) => {
      const body = {
        outcome: outcome as "RESOLVED" | "REJECTED",
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(hideRatingId.trim() ? { hideRatingId: hideRatingId.trim() } : {}),
        ...(suspendUserId.trim()
          ? { suspendUserId: suspendUserId.trim() }
          : {}),
        ...(refund.trim() ? { refundCreditCount: Number(refund) } : {}),
      };
      return getSdk().admin.resolveAbuse(id, body);
    },
    onSuccess: () => {
      toast({ tone: "success", title: t("abuseResolveSuccess") });
      onResolved();
    },
    onError: () => toast({ tone: "error", title: t("abuseGenericError") }),
  });

  if (!report) return null;

  function submit() {
    if (outcome !== "RESOLVED" && outcome !== "REJECTED") {
      toast({ tone: "error", title: t("abuseResolveErrorOutcome") });
      return;
    }
    if (hideRatingId.trim() && !UUID_RE.test(hideRatingId.trim())) {
      toast({ tone: "error", title: t("abuseResolveErrorUuid") });
      return;
    }
    if (suspendUserId.trim() && !UUID_RE.test(suspendUserId.trim())) {
      toast({ tone: "error", title: t("abuseResolveErrorUuid") });
      return;
    }
    mut.mutate(report!.id);
  }

  return (
    <Dialog
      open={!!report}
      onClose={onClose}
      title={t("abuseResolveTitle")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            {t("abuseResolveCancel")}
          </Button>
          <Button onClick={submit} loading={mut.isPending}>
            {t("abuseResolveSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <Label>{t("abuseResolveOutcome")}</Label>
          <div className="mt-2 flex flex-col gap-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-mist px-3 py-2 hover:bg-mist/40">
              <input
                type="radio"
                name="outcome"
                checked={outcome === "RESOLVED"}
                onChange={() => setOutcome("RESOLVED")}
              />
              <CheckCircle2 size={14} className="text-emerald-700" />
              <span className="text-sm">
                {t("abuseResolveOutcomeResolved")}
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-mist px-3 py-2 hover:bg-mist/40">
              <input
                type="radio"
                name="outcome"
                checked={outcome === "REJECTED"}
                onChange={() => setOutcome("REJECTED")}
              />
              <span className="text-sm">
                {t("abuseResolveOutcomeRejected")}
              </span>
            </label>
          </div>
        </div>

        <div>
          <Label htmlFor="abuse-note">{t("abuseResolveNote")}</Label>
          <Textarea
            id="abuse-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("abuseResolveNotePh")}
            rows={3}
            maxLength={2000}
          />
        </div>

        <div>
          <Label htmlFor="abuse-hide-rating">
            {t("abuseResolveHideRating")}
          </Label>
          <Input
            id="abuse-hide-rating"
            value={hideRatingId}
            onChange={(e) => setHideRatingId(e.target.value)}
            placeholder="uuid"
          />
          <p className="mt-1 text-[11px] text-ash">
            {t("abuseResolveHideRatingHint")}
          </p>
        </div>

        <div>
          <Label htmlFor="abuse-suspend-user">
            {t("abuseResolveSuspendUser")}
          </Label>
          <Input
            id="abuse-suspend-user"
            value={suspendUserId}
            onChange={(e) => setSuspendUserId(e.target.value)}
            placeholder="uuid"
          />
          <p className="mt-1 text-[11px] text-ash">
            {t("abuseResolveSuspendUserHint")}
          </p>
        </div>

        <div>
          <Label htmlFor="abuse-refund">{t("abuseResolveRefund")}</Label>
          <Input
            id="abuse-refund"
            type="number"
            min={0}
            max={100}
            value={refund}
            onChange={(e) => setRefund(e.target.value)}
            placeholder="0"
          />
          <p className="mt-1 text-[11px] text-ash">
            {t("abuseResolveRefundHint")}
          </p>
        </div>
      </div>
    </Dialog>
  );
}
